import { loadScript } from './loadScript'
import { hasConsent, onConsentChange } from './consent'

let initialised = false

// Standard Facebook Pixel base code stub: queues calls on `fbq.queue` until
// the real fbevents.js script loads and replays them via `callMethod`.
const installStub = (): void => {
  interface FbqStub {
    (...args: unknown[]): void
    callMethod?: (...args: unknown[]) => void
    queue: unknown[][]
    push: FbqStub
    loaded: boolean
    version: string
  }

  const fbq = function (this: FbqStub, ...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args)
    } else {
      fbq.queue.push(args)
    }
  } as FbqStub
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  window.fbq = fbq
}

// Loads fbevents.js and revokes consent on every visit, regardless of the
// visitor's decision — Meta's documented consent-signal API (FR-011/FR-012),
// distinct from application-level Tracking Event data, which stays gated
// behind hasConsent('marketing') (trackFacebookPageView below, unchanged).
const initFacebookPixel = (): void => {
  if (initialised || typeof window === 'undefined') {
    return
  }
  const pixelId = process.env.GATSBY_FACEBOOK_PIXEL_ID
  if (!pixelId) {
    return
  }
  initialised = true

  try {
    installStub()
    // Must be called before 'init' — Meta's documented pattern for custom
    // consent banners, guaranteeing no cookies/requests until 'grant'
    // (research.md §6).
    window.fbq('consent', 'revoke')
    window.fbq('init', pixelId)
    // No PageView here — fired from the consent-grant transition below,
    // once marketing consent actually allows it.

    loadScript(
      'facebook-pixel',
      'https://connect.facebook.net/en_US/fbevents.js'
    )
  } catch {
    // A broken Facebook Pixel loader must never break page rendering (FR-012).
  }
}

export const registerFacebookPixelLoader = (): void => {
  initFacebookPixel()

  // fbq('consent', 'revoke') above already declared "not granted", so the
  // baseline here is denied — only a transition away from that is worth
  // signaling.
  let lastMarketingGranted = false

  onConsentChange((state) => {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
      return
    }
    if (lastMarketingGranted === state.marketing) {
      return
    }
    const wasGranted = lastMarketingGranted
    lastMarketingGranted = state.marketing

    try {
      window.fbq('consent', state.marketing ? 'grant' : 'revoke')
      if (state.marketing && !wasGranted) {
        window.fbq('track', 'PageView')
      }
    } catch {
      // A broken consent signal must never break page rendering (FR-012).
    }
  })
}

// The live GTM "Facebook Pixel" tag fires PageView on every route change (in
// addition to once when consent is granted) — no other events. Mirrored here
// via gatsby-browser.tsx's onRouteUpdate calling this alongside GA4's
// trackPageView. No-ops until fbq has initialised (i.e. until marketing
// consent is granted at least once).
export const trackFacebookPageView = (): void => {
  if (
    typeof window === 'undefined' ||
    typeof window.fbq !== 'function' ||
    !hasConsent('marketing')
  ) {
    return
  }
  window.fbq('track', 'PageView')
}
